import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { QueryParams } from './query-params';
import { Observable, Subject } from 'rxjs';
import { switchMap, filter, takeUntil, tap } from 'rxjs/operators';
import { ConfigService } from './config.service';
import { AuthenticationService } from '../authentication/authentication.service';
import { Body, SearchQuery } from '../../models/body';
import InterfaceMetricsHttpService from '@/app/core/http/http-service.interface';
import InterfaceHttpService from '@/app/core/http/http-service.interface';
import { LogService } from '@/app/core/services/log.service';

const DEBUG_HEADERS = false;
import { RequestCancellationService } from './request-cancellation.service';

@Injectable({
  providedIn: 'root',
})
export class SimpleMetricsHttpService implements InterfaceMetricsHttpService {
  protected shouldAbort: boolean = true;
  protected cancelSubject$: Subject<any> | null = null;

  constructor(
    private http: HttpClient,
    private authenticationService: AuthenticationService,
    private config: ConfigService,
    private cancellationService: RequestCancellationService,
    private logger: LogService
  ) {}

  /**
   * Gets the route cancellation subject for use in wrapper services.
   * @internal Used by MetricsHttpService if needed.
   */
  public getRouteCancellationSubject(): Subject<void> | undefined {
    return this.cancellationService.getRouteCancellationSubject(
      this.shouldAbort
    );
  }

  /**
   * Disables abort functionality for all requests on this instance.
   * Use with clone() for isolation: service.clone().disableRoutingAbort().get(...)
   */
  public disableRoutingAbort(): InterfaceMetricsHttpService {
    this.shouldAbort = false;
    return this;
  }

  public setCancelSubject(sub$: Subject<any>): InterfaceHttpService {
    this.cancelSubject$ = sub$;
    return this;
  }

  /**
   * Creates a new instance of SimpleMetricsHttpService with the same dependencies.
   * This allows you to call disableRoutingAbort() on the cloned instance
   * without affecting other requests using the original singleton service.
   *
   * @returns A new SimpleMetricsHttpService instance
   *
   * @example
   * this.simpleMetricsHttpService.clone().disableRoutingAbort().get('/endpoint')
   */
  public clone(): SimpleMetricsHttpService {
    return new SimpleMetricsHttpService(
      this.http,
      this.authenticationService,
      this.config,
      this.cancellationService,
      this.logger
    );
  }

  /**
   * Returns cancellation subject based on shouldAbort flag and any custom cancel subject.
   */
  private getCancelSubject(): Subject<any> | undefined {
    return this.cancellationService.getCancellationSubject(
      this.shouldAbort,
      this.cancelSubject$
    );
  }

  public delete = (...args: any[]): Observable<Record<any, any>> => {
    const cancel$ = this.getCancelSubject();

    return this.authenticationService.getAuthToken().pipe(
      switchMap((token) => {
        const endpoint = args[0].indexOf('/') === 0 ? args[0] : '/' + args[0];
        const body = args[1] || {};
        return this.config.baseMetricsApiUrl.pipe(
          switchMap((apiUrl) => {
            const url = apiUrl + endpoint;
            const headers = this.getHeaders(
              token.AccessToken,
              this.authenticationService._userInfoView?.SelectedClient?.ClientID
            );
            const obs = this.http.delete(url, {
              headers,
              body,
              withCredentials: true,
            });
            return cancel$
              ? obs.pipe(
                  takeUntil(cancel$),
                  tap({
                    complete: () => {
                      if (cancel$.isStopped || cancel$.observers.length === 0) {
                        this.logger.warn(`[HTTP CANCELLED]: DELETE ${url}`);
                      }
                    },
                  })
                )
              : obs;
          })
        );
      })
    );
  };

  public get = (...args: any[]): Observable<Record<any, any>> => {
    const cancel$ = this.getCancelSubject();

    return this.authenticationService.getAuthToken().pipe(
      switchMap((token) => {
        const endpoint = args[0].indexOf('/') === 0 ? args[0] : '/' + args[0];
        args[1] = args[1] || {};
        let searchParams = new HttpParams();
        if (args.length > 1 && args[1]) {
          searchParams = this.toHttpParams(args[1].search as QueryParams);
        }
        return this.config.baseMetricsApiUrl.pipe(
          switchMap((apiUrl) => {
            if (token?.AccessToken) {
              const url = apiUrl + endpoint;
              const headers = this.getHeaders(
                token.AccessToken,
                this.authenticationService._userInfoView?.SelectedClient
                  ?.ClientID
              );
              const obs = this.http.get(url, {
                headers,
                params: searchParams,
                withCredentials: true,
              });
              return cancel$
                ? obs.pipe(
                    takeUntil(cancel$),
                    tap({
                      complete: () => {
                        if (
                          cancel$.isStopped ||
                          cancel$.observers.length === 0
                        ) {
                          this.logger.warn(`[HTTP CANCELLED]: GET ${url}`);
                        }
                      },
                    })
                  )
                : obs;
            }
            return new Observable<any>(); // empty if no token
          })
        );
      })
    );
  };

  public getHeaders(accessToken: string, clientId?: number): HttpHeaders {
    let headers = new HttpHeaders().set(
      'Authorization',
      `Bearer ${accessToken}`
    );
    if (clientId) {
      headers = headers.append('ClientId', clientId.toString());
    }

    if (DEBUG_HEADERS) {
      this.logger.info('simple-metrics-http.service.getHeaders');
      const keys = headers.keys();
      keys.forEach((key) => {
        this.logger.info(`       ${key} = ${headers.get(key)}`);
      });
    }

    return headers;
  }

  public getWithoutAuthentication = (
    ...args: any[]
  ): Observable<Record<any, any>> => {
    const cancel$ = this.getCancelSubject();

    return this.config.baseMetricsApiUrl.pipe(
      switchMap((apiUrl) => {
        const endpoint = args[0].indexOf('/') === 0 ? args[0] : '/' + args[0];
        const url = apiUrl + endpoint;
        args[1] = args[1] || {};
        let searchParams = new HttpParams();
        if (args.length > 1 && args[1]) {
          searchParams = this.toHttpParams(args[1].search as QueryParams);
        }
        const obs = this.http.get(url, {
          params: searchParams,
          withCredentials: true,
        });
        return cancel$
          ? obs.pipe(
              takeUntil(cancel$),
              tap({
                complete: () => {
                  if (cancel$.isStopped || cancel$.observers.length === 0) {
                    console.warn(
                      `[HTTP CANCELLED]: getWithoutAuthentication ${url}`
                    );
                  }
                },
              })
            )
          : obs;
      })
    );
  };

  public patch = (...args: any[]): Observable<Record<any, any>> => {
    const cancel$ = this.getCancelSubject();

    return this.authenticationService.getAuthToken().pipe(
      switchMap((token) => {
        const endpoint = args[0].indexOf('/') === 0 ? args[0] : '/' + args[0];
        args[1] = args[1] || {};
        return this.config.baseMetricsApiUrl.pipe(
          switchMap((apiUrl) => {
            const url = apiUrl + endpoint;
            // const headers = new HttpHeaders().set(
            //   'Authorization',
            //   `Bearer ${token.AccessToken}`
            // );
            const headers = this.getHeaders(
              token.AccessToken,
              this.authenticationService._userInfoView?.SelectedClient?.ClientID
            );
            const obs = this.http.patch(url, args[1], {
              withCredentials: true,
              headers,
            });
            return cancel$
              ? obs.pipe(
                  takeUntil(cancel$),
                  tap({
                    complete: () => {
                      if (cancel$.isStopped || cancel$.observers.length === 0) {
                        this.logger.warn(`[HTTP CANCELLED]: PATCH ${url}`);
                      }
                    },
                  })
                )
              : obs;
          })
        );
      })
    );
  };

  public put = (...args: any[]): Observable<Record<any, any>> => {
    const cancel$ = this.getCancelSubject();

    return this.authenticationService.getAuthToken().pipe(
      switchMap((token) => {
        const endpoint = args[0].indexOf('/') === 0 ? args[0] : '/' + args[0];
        args[1] = args[1] || {};
        const body = { ...args[1] };
        return this.config.baseMetricsApiUrl.pipe(
          switchMap((apiUrl) => {
            const url = apiUrl + endpoint;
            const headers = this.getHeaders(
              token.AccessToken,
              this.authenticationService._userInfoView?.SelectedClient?.ClientID
            );
            const obs = this.http.put(url, body, {
              headers,
              withCredentials: true,
            });
            return cancel$
              ? obs.pipe(
                  takeUntil(cancel$),
                  tap({
                    complete: () => {
                      if (cancel$.isStopped || cancel$.observers.length === 0) {
                        this.logger.warn(`[HTTP CANCELLED]: PUT ${url}`);
                      }
                    },
                  })
                )
              : obs;
          })
        );
      })
    );
  };

  public post = (
    endpoint: string,
    content?: Body,
    overrideSearchQuery?: SearchQuery
  ): Observable<Record<any, any>> => {
    const cancel$ = this.getCancelSubject();

    endpoint = endpoint.indexOf('/') === 0 ? endpoint : '/' + endpoint;
    return this.authenticationService.getAuthToken().pipe(
      switchMap((token) => {
        if (token && token.AccessToken) {
          const postBody = { ...content };

          const headers = this.getHeaders(
            token.AccessToken,
            this.authenticationService._userInfoView?.SelectedClient?.ClientID
          );
          return this.config.baseMetricsApiUrl.pipe(
            switchMap((apiUrl) => {
              const url = apiUrl + endpoint;
              const obs = this.http.post(url, postBody, {
                headers,
                withCredentials: true,
              });
              return cancel$
                ? obs.pipe(
                    takeUntil(cancel$),
                    tap({
                      complete: () => {
                        if (
                          cancel$.isStopped ||
                          cancel$.observers.length === 0
                        ) {
                          this.logger.warn(`[HTTP CANCELLED]: POST ${url}`);
                        }
                      },
                    })
                  )
                : obs;
            })
          );
        }
        return new Observable<any>();
      })
    );
  };

  public postWithoutAuthentication = (
    endpoint: string,
    content?: Body,
    overrideSearchQuery?: SearchQuery
  ): Observable<Record<any, any>> => {
    const cancel$ = this.getCancelSubject();
    endpoint = !endpoint.indexOf('/') ? endpoint : '/' + endpoint;

    const postBody = { ...content };

    return this.config.baseMetricsApiUrl.pipe(
      switchMap((apiUrl) => {
        const url = apiUrl + endpoint;
        const obs = this.http.post(url, postBody);
        return cancel$
          ? obs.pipe(
              takeUntil(cancel$),
              tap({
                complete: () => {
                  if (cancel$.isStopped || cancel$.observers.length === 0) {
                    this.logger.warn(
                      `[HTTP CANCELLED]: postWithoutAuthentication ${url}`
                    );
                  }
                },
              })
            )
          : obs;
      })
    );
  };

  /**
   * getFile XHR logic for file download: optional, your logic is fine.
   * You *could* refactor this to use HttpClient’s Blob/getStream, but the abort logic is correct for XHR.
   */
  public getFile = (endpoint: string): Observable<Record<any, any>[]> => {
    const cancel$ = this.getCancelSubject();

    return Observable.create((observer) => {
      this.authenticationService.getAuthToken().subscribe((token) => {
        endpoint = endpoint.indexOf('/') === 0 ? endpoint : '/' + endpoint;
        if (
          this.authenticationService &&
          this.authenticationService._userInfoView &&
          this.authenticationService._userInfoView.SelectedClient
        ) {
          let delimit = '?';
          if (endpoint.toLowerCase().indexOf('clientid=') === -1) {
            if (endpoint.indexOf('?') > -1) {
              delimit = '&';
            }
            endpoint =
              endpoint +
              '?' +
              'ClientId=' +
              this.authenticationService._userInfoView.SelectedClient.ClientID.toString();
          }
        }
        this.config.baseMetricsApiUrl.subscribe((apiUrl) => {
          const url = apiUrl + endpoint;
          const xhr = new XMLHttpRequest();
          xhr.open('GET', url, true);

          let aborted = false;
          // === MANUAL ABORT LOGIC FOR XHR (keep as-is, or update to match RxJS cancellation pattern) ===
          if (cancel$) {
            // abort$ triggers cancellation
            const sub = cancel$.subscribe(() => {
              if (xhr.readyState !== 4) {
                aborted = true;
                xhr.abort();
                observer.complete();
              }
            });
          }

          xhr.setRequestHeader('Authorization', 'Bearer ' + token.AccessToken);
          xhr.withCredentials = true;
          xhr.responseType = 'blob';
          xhr.onreadystatechange = () => {
            if (xhr.readyState === 4 && !aborted) {
              if (xhr.status === 200) {
                const contentType =
                  xhr.getResponseHeader('content-type') ||
                  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
                const blob = new Blob([xhr.response], { type: contentType });
                observer.next(blob);
                observer.complete();
              } else {
                if (xhr.status !== 0) {
                  observer.error(xhr.response);
                }
              }
            }
          };
          xhr.send();
        });
      });
    });
  };

  public toHttpParams = (params: QueryParams): HttpParams => {
    if (!params) {
      return new HttpParams();
    }
    let httpParams = new HttpParams();
    Array.from(params.paramsMap.entries()).forEach((entry) => {
      if (entry[1].length === 1) {
        httpParams = httpParams.append(entry[0], entry[1][0]);
      } else {
        entry[1].forEach((item) => {
          httpParams = httpParams.append(entry[0], item);
        });
      }
    });
    return httpParams;
  };
}
